import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";

// Iter-205 — Supplier directory CRUD. Lives under the same module as outgoing
// POs and is accessible to anyone with `manage_outgoing_pos` perm.

const EMPTY_FORM = {
  name: "",
  ice: "",
  address: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  default_currency: "MAD",
  notes: "",
};

export default function Suppliers() {
  useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | {... form}
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/suppliers");
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger les fournisseurs");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name?.trim()) return toast.error("Le nom est requis.");
    try {
      const payload = { ...editing };
      // Remove ID-only field before save
      delete payload.supplier_id;
      delete payload.created_at;
      delete payload.created_by;
      delete payload.updated_at;
      delete payload.updated_by;
      if (editing.supplier_id) {
        await api.patch(`/suppliers/${editing.supplier_id}`, payload);
        toast.success("Fournisseur mis à jour");
      } else {
        await api.post("/suppliers", payload);
        toast.success("Fournisseur créé");
      }
      setEditing(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map(x => x.msg || x).join(", ") : (d || "Échec de la sauvegarde");
      toast.error(msg);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer le fournisseur « ${s.name} » ?`)) return;
    try {
      await api.delete(`/suppliers/${s.supplier_id}`);
      toast.success("Fournisseur supprimé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la suppression");
    }
  };

  const filtered = items.filter((s) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (s.name || "").toLowerCase().includes(q) || (s.contact_email || "").toLowerCase().includes(q);
  });

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px] space-y-6" data-testid="suppliers-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Fournisseurs</h1>
          <p className="text-sm text-zinc-500 mt-1">Carnet d'adresses utilisé pour émettre les bons de commande sortants.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher…"
            className="w-60"
            data-testid="suppliers-search"
          />
          <Button onClick={() => setEditing({ ...EMPTY_FORM })} data-testid="supplier-new-btn">
            <Plus size={14} className="mr-1.5" /> Nouveau fournisseur
          </Button>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="text-sm text-zinc-500 text-center py-16">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-zinc-500 text-center py-16 border border-dashed border-zinc-200 rounded-lg">
          {items.length === 0 ? "Aucun fournisseur. Créez-en un pour commencer." : "Aucun résultat."}
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-lg overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                <tr>
                  <th className="text-left px-4 py-2">Nom</th>
                  <th className="text-left px-4 py-2">ICE</th>
                  <th className="text-left px-4 py-2">Contact</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.map((s) => (
                  <tr key={s.supplier_id} className="hover:bg-zinc-50/50" data-testid={`supplier-row-${s.supplier_id}`}>
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      <Building2 size={14} className="text-zinc-400" />
                      {s.name}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-600">{s.ice || "—"}</td>
                    <td className="px-4 py-3">{s.contact_name || "—"}</td>
                    <td className="px-4 py-3 text-xs text-zinc-600 font-mono">{s.contact_email || "—"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => setEditing({ ...s })} data-testid={`supplier-edit-${s.supplier_id}`}>
                        <Pencil size={12} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(s)} className="text-rose-600 hover:text-rose-700" data-testid={`supplier-delete-${s.supplier_id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit / Create dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent data-testid="supplier-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.supplier_id ? "Modifier le fournisseur" : "Nouveau fournisseur"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Nom <span className="text-red-600">*</span></Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="supplier-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>ICE</Label>
                  <Input value={editing.ice || ""} onChange={(e) => setEditing({ ...editing, ice: e.target.value })} data-testid="supplier-form-ice" />
                </div>
                <div><Label>Devise par défaut</Label>
                  <Input value={editing.default_currency || ""} onChange={(e) => setEditing({ ...editing, default_currency: e.target.value })} placeholder="MAD" />
                </div>
              </div>
              <div><Label>Adresse</Label>
                <Textarea value={editing.address || ""} onChange={(e) => setEditing({ ...editing, address: e.target.value })} rows={2} data-testid="supplier-form-address" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nom du contact</Label>
                  <Input value={editing.contact_name || ""} onChange={(e) => setEditing({ ...editing, contact_name: e.target.value })} data-testid="supplier-form-contact-name" />
                </div>
                <div><Label>Téléphone</Label>
                  <Input value={editing.contact_phone || ""} onChange={(e) => setEditing({ ...editing, contact_phone: e.target.value })} />
                </div>
              </div>
              <div><Label>Email du contact</Label>
                <Input type="email" value={editing.contact_email || ""} onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} data-testid="supplier-form-contact-email" />
              </div>
              <div><Label>Notes</Label>
                <Textarea value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} rows={2} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={save} data-testid="supplier-save-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
